/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { IEvent } from '../@types/event';
import type { RoomData } from '../xhubchat';
import type { IIndexedDBBackend } from './interfaces/indexeddb-backend';
import type { IStoredClientOpts } from './interfaces/store';
import type { IOpts as IMemoryOpts } from './memory.ts';

import { XHubChatEvent } from '../models/event/event';
import { logger } from '../utils/logger';
// eslint-disable-next-line sort-imports
import { sleep, TypedEventEmitter } from '../xhubchat';
import { pendingEventsKey } from './helper/utils';
import { LocalIndexedDBStoreBackend } from './indexeddb-local-backend';
import { RemoteIndexedDBStoreBackend } from './indexeddb-remote-backend';
import { MemoryStore } from './memory';

type IOpts = {
  /** The Indexed DB interface e.g. `window.indexedDB` */
  indexedDB?: IDBFactory;
  /** Optional database name. The same name must be used to open the same database. */
  dbName?: string;
  /** Optional factory to spin up a Worker to execute the IDB transactions within. */
  workerFactory?: () => Worker;
  /**
   * Optional user ID for database isolation.
   * When provided, creates a separate database per user to prevent data leaks.
   * Highly recommended for multi-user environments.
   */
  userId?: string;
} & IMemoryOpts;

type EventHandlerMap = {
  // Fired when an IDB command fails on a degradable path, and the store falls back to MemoryStore
  // This signals the potential for data volatility.
  degraded: (e: Error) => void;
  // Fired when the IndexedDB gets closed unexpectedly, for example, if the underlying storage is removed or
  // if the user clears the database in the browser's history preferences.
  closed: () => void;
};

// If this value is too small we'll be writing very often which will cause
// noticeable stop-the-world pauses. If this value is too big we'll be writing
// so infrequently that the /sync size gets bigger on reload. Writing more
// often does not affect the length of the pause since the entire /sync
// response is persisted each time.
const WRITE_DELAY_MS = 1000 * 60 * 5; // once every 5 minutes

type DegradableFn<A extends Array<any>, T> = (...args: A) => Promise<T>;

export class IndexedDBStore extends MemoryStore {
  /**
   * Check if an IndexedDB database with the given name exists.
   * @param indexedDB - The IndexedDB factory (usually window.indexedDB)
   * @param dbName - The name of the database to check
   * @param userId - Optional user ID to check for user-specific database
   * @returns A promise that resolves to true if the database exists, false otherwise.
   */
  public static exists(indexedDB: IDBFactory, dbName: string, userId?: string): Promise<boolean> {
    return LocalIndexedDBStoreBackend.exists(indexedDB, dbName, userId);
  }

  /**
   * The backend instance.
   * Call through to this API if you need to perform specific indexeddb actions like deleting the database.
   */
  public readonly backend: IIndexedDBBackend;

  /**
   * Event emitter for internal events.
   */
  private emitter = new TypedEventEmitter<keyof EventHandlerMap, EventHandlerMap>();

  /**
   * True if `startup()` has been called and resolved successfully.
   */
  private startedUp = false;
  private syncTs = 0;

  private userModifiedMap: Record<string, number> = {}; // user_id : timestamp

  /**
   * Construct a new Indexed Database store, which extends MemoryStore.
   *
   * This store functions like a MemoryStore except it periodically persists
   * the contents of the store to an IndexedDB backend.
   *
   * All data is still kept in-memory but can be loaded from disk by calling
   * `startup()`. This can make startup times quicker as a complete
   * sync from the server is not required. This does not reduce memory usage as all
   * the data is eagerly fetched when `startup()` is called.
   * ```
   * let opts = { indexedDB: window.indexedDB, localStorage: window.localStorage };
   * let store = new IndexedDBStore(opts);
   * let client = sdk.createClient({
   *     store: store,
   * });
   * await store.startup(); // load from indexed db, must be called after createClient
   * client.startClient();
   * client.on("sync", function(state, prevState, data) {
   *     if (state === "PREPARED") {
   *         console.log("Started up, now with go faster stripes!");
   *     }
   * });
   * ```
   *
   * @param opts - Options object.
   */
  public constructor(opts: IOpts) {
    super(opts);

    if (!opts.indexedDB) {
      throw new Error('Missing required option: indexedDB');
    }

    if (opts.workerFactory) {
      this.backend = new RemoteIndexedDBStoreBackend(opts.workerFactory, opts.dbName, opts.userId);
    } else {
      this.backend = new LocalIndexedDBStoreBackend(opts.indexedDB, opts.dbName, opts.userId);
    }
  }

  /**
   * @returns Resolved when loaded from indexed db.
   */
  public async startup(): Promise<void> {
    if (this.startedUp) {
      logger.log(`IndexedDBStore.startup: already started`);
      return Promise.resolve();
    }

    logger.log(`IndexedDBStore.startup: connecting to backend`);
    return (this.backend.connect(this.onClose))
      .then(async () => {
        await sleep(80);
        return this.backend.getUserPresenceEvents();
      })
      .then((userPresenceEvents) => {
        logger.log(`IndexedDBStore.startup: processing presence events`);
        userPresenceEvents.forEach(([userId, rawEvent, userdata]) => {
          if (!this.createUser) throw new Error('`IndexedDBStore.startup` must be called after assigning it to the client, not before!');

          const u = this.createUser(userId, userId);

          if (userdata) {
            u.setAvatarUrl(userdata.avatarUrl);
            u.setDisplayName(userdata.displayName);
            u.setDescription(userdata.description);
            u.setRawDisplayName(userdata.rawDisplayName);
          }

          if (rawEvent) {
            u.setPresenceEvent(new XHubChatEvent(rawEvent));
          }
          this.userModifiedMap[u.userId] = u.getLastModifiedTime();
          this.storeUser(u);
        });
        this.startedUp = true;
      });
  }

  /*
     * Close the database and destroy any associated workers
     */
  public destroy(): Promise<void> {
    return this.backend.destroy();
  }

  /**
   * Called when the backend detects that the connection
   */
  private onClose = (): void => {
    this.emitter.emit('closed');
  };

  /**
   * Delete all data from this store.
   * @returns Promise which resolves if the data was deleted from the database.
   */
  public deleteAllData = this.degradable((): Promise<void> => {
    super.deleteAllData();
    return this.backend.clearDatabase().then(
      () => {
        logger.info('Deleted indexeddb data.');
      },
      (err) => {
        logger.error(`Failed to delete indexeddb data: ${err}`);
        throw err;
      },
    );
  }, null);

  /**
   * All member functions of `IndexedDBStore` that access the backend use this wrapper to
   * watch for failures after initial store startup, including `QuotaExceededError` as
   * free disk space changes, etc.
   *
   * When IndexedDB fails via any of these paths, we degrade this back to a `MemoryStore`
   * in place so that the current operation and all future ones are in-memory only.
   *
   * @param func - The degradable work to do.
   * @param fallback - The method name for fallback.
   * @returns A wrapped member function.
   */
  private degradable<A extends Array<any>, F extends keyof MemoryStore | null, R = void>(
    func: DegradableFn<A, R>,
    fallback: F,
  ): DegradableFn<A, F extends string ? R : void> {
    const fallbackFn = fallback ? (super[fallback] as (...args: A) => Promise<R>) : null;

    return (async (...args) => {
      try {
        return await func.call(this, ...args);
      } catch (e) {
        logger.error('IndexedDBStore failure, degrading to MemoryStore', e);
        this.emitter.emit('degraded', e as Error);
        try {
          // We try to delete IndexedDB after degrading since this store is only a
          // cache (the app will still function correctly without the data).
          // It's possible that deleting repair IndexedDB for the next app load,
          // potentially by making a little more space available.
          logger.log('IndexedDBStore trying to delete degraded data');
          await this.backend.clearDatabase();
          logger.log('IndexedDBStore delete after degrading succeeded');
        } catch (e) {
          logger.warn('IndexedDBStore delete after degrading failed', e);
        }
        // Degrade the store from being an instance of `IndexedDBStore` to instead be
        // an instance of `MemoryStore` so that future API calls use the memory path
        // directly and skip IndexedDB entirely. This should be safe as
        // `IndexedDBStore` already extends from `MemoryStore`, so we are making the
        // store become its parent type in a way. The mutator methods of
        // `IndexedDBStore` also maintain the state that `MemoryStore` uses (many are
        // not overridden at all).
        if (fallbackFn) {
          return fallbackFn.call(this, ...args);
        }
      }
    }) as DegradableFn<A, F extends string ? R : void>;
  }

  /**
   * ======================================================================
   *                            EVENTS
   * ======================================================================
   */
  // XXX: ideally these would be stored in indexeddb as part of the room but,
  // we don't store rooms as such and instead accumulate entire sync responses atm.
  public async getPendingEvents(roomId: string): Promise<Partial<IEvent>[]> {
    if (!this.localStorage) return super.getPendingEvents(roomId);

    const serialized = this.localStorage.getItem(pendingEventsKey(roomId));
    if (serialized) {
      try {
        return JSON.parse(serialized);
      } catch (e) {
        logger.error('Could not parse persisted pending events', e);
      }
    }
    return [];
  }

  public async setPendingEvents(roomId: string, events: Partial<IEvent>[]): Promise<void> {
    if (!this.localStorage) return super.setPendingEvents(roomId, events);

    if (events.length > 0) {
      this.localStorage.setItem(pendingEventsKey(roomId), JSON.stringify(events));
    } else {
      this.localStorage.removeItem(pendingEventsKey(roomId));
    }
  }
  /**
   * ======================================================================
   *                           END EVENTS
   * ======================================================================
   */

  /**
   * ======================================================================
   *                            CLIENT OPTIONS
   * ======================================================================
   */
  public getClientOptions = this.degradable((): Promise<IStoredClientOpts | undefined> => {
    return this.backend.getClientOptions();
  }, 'getClientOptions');

  public storeClientOptions = this.degradable((options: IStoredClientOpts): Promise<void> => {
    super.storeClientOptions(options);
    return this.backend.storeClientOptions(options);
  }, 'storeClientOptions');
  /**
   * ======================================================================
   *                          END CLIENT OPTIONS
   * ======================================================================
   */

  public setRoomsData = this.degradable((roomsData: Record<string, RoomData>): Promise<void> => {
    return this.backend.setRoomsData(roomsData);
  }, 'setRoomsData');

  public getSavedRooms = this.degradable((): Promise<RoomData[] | null> => {
    return this.backend.getSavedRooms();
  }, 'getSavedRooms');

  /**
   * Whether this store would like to save its data
   * Note that obviously whether the store wants to save or
   * not could change between calling this function and calling
   * save().
   *
   * @returns True if calling save() will actually save
   *     (at the time this function is called).
   */
  public wantsSave(): boolean {
    const now = Date.now();
    return now - this.syncTs > WRITE_DELAY_MS;
  }

  /**
   * Possibly write data to the database.
   *
   * @param force - True to force a save to happen
   * @returns Promise resolves after the write completes
   *     (or immediately if no write is performed)
   */
  public async save(force = false): Promise<void> {
    if (force || this.wantsSave()) {
      return await this.reallySave();
    }
    return Promise.resolve();
  }

  private reallySave = this.degradable((): Promise<void> => {
    this.syncTs = new Date().getTime(); // set now to guard against multi-writes

    // work out changed users (this doesn't handle deletions but you
    // can't 'delete' users as they are just presence events).
    const userTuples: [userId: string, presenceEvent: Partial<IEvent>, userData?: Record<string, any>][] = [];
    for (const u of this.getUsers()) {
      if (this.userModifiedMap[u.userId] === u.getLastModifiedTime()) continue;
      if (!u.events.presence) continue;

      userTuples.push([u.userId, u.events.presence.event, u.getUserData()]);
      // note that we've saved this version of the user
      this.userModifiedMap[u.userId] = u.getLastModifiedTime();
    }

    return this.backend.syncToDatabase(userTuples);
  }, null);
}
